import React, { useState, useEffect, useCallback } from 'react';
import Landing from './components/Landing';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PatientForm from './components/PatientForm';
import PatientsHistory from './components/PatientsHistory';
import LaboratoriesDashboard from './components/LaboratoriesDashboard';
import AuditLogPanel from './components/AuditLogPanel';
import RecetaPreview from './components/RecetaPreview';
import { authService, consultationService } from './services/api';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [currentView, setCurrentView] = useState('landing');
  const [editingConsultationId, setEditingConsultationId] = useState(null);
  const [editingSessionKey, setEditingSessionKey] = useState(null);
  const [consultationReturnView, setConsultationReturnView] = useState('dashboard');
  const [activeDrafts, setActiveDrafts] = useState([]);
  const [calibrationDoctor, setCalibrationDoctor] = useState(null);

  useEffect(() => {
    if (authService.isAuthenticated()) {
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);
      setCurrentView('dashboard');
    }
  }, []);

  const loadActiveDrafts = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setActiveDrafts([]);
      return;
    }

    try {
      const result = await consultationService.getActiveDrafts();
      if (result.success) {
        setActiveDrafts(result.data || []);
      }
    } catch (error) {
      setActiveDrafts([]);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setActiveDrafts([]);
      return;
    }

    loadActiveDrafts();
  }, [user, currentView, loadActiveDrafts]);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setCurrentView('dashboard');
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setEditingConsultationId(null);
    setEditingSessionKey(null);
    setActiveDrafts([]);
    setConsultationReturnView('dashboard');
    setCurrentView('landing');
  };

  const handleNewConsultation = () => {
    setEditingConsultationId(null);
    setEditingSessionKey(null);
    setConsultationReturnView('dashboard');
    setCurrentView('new-consultation');
  };

  const handleBackToDashboard = () => {
    setEditingConsultationId(null);
    setEditingSessionKey(null);
    setConsultationReturnView('dashboard');
    setCurrentView('dashboard');
  };

  const handleExitConsultation = () => {
    setCurrentView(consultationReturnView);
    setEditingConsultationId(null);
    setEditingSessionKey(null);
    setConsultationReturnView('dashboard');
  };

  const handleResumeConsultation = (consultationId, sessionKey = null, returnView = 'patients-history') => {
    setEditingConsultationId(consultationId || null);
    setEditingSessionKey(sessionKey);
    setConsultationReturnView(returnView);
    setCurrentView('new-consultation');
  };

  const handleResumeDraft = (draft) => {
    handleResumeConsultation(
      draft?.draft_consultation_id || null,
      draft?.session_key || null,
      'dashboard'
    );
  };

  const handleViewPatients = () => {
    setCurrentView('patients-history');
  };

  const handleOpenCalibrationReceta = async () => {
    // Obtener datos reales del doctor (cedula, universidad) para la receta demo
    try {
      const result = await authService.getDoctorInfo();
      if (result.success) {
        setCalibrationDoctor(result.data);
      }
    } catch (error) {
      setCalibrationDoctor(null);
    }
    setCurrentView('receta-calibration');
  };

  const handleViewLaboratories = () => {
    setCurrentView('laboratories-dashboard');
  };

  const canViewAuditLog = user && (user.rol === 'admin' || user.username === 'dr.oscar');

  const handleViewAuditLog = () => {
    setCurrentView('audit-log');
  };

  const buildCalibrationConsultation = () => ({
    id: 'CAL-001',
    folio: 'CAL-001',
    nombre: 'Paciente Calibración Receta',
    fecha_nacimiento: '1990-01-15',
    fecha_ingreso: new Date().toISOString(),
    tension_arterial_sistolica: '118',
    tension_arterial_diastolica: '76',
    peso: '64',
    temperatura: '36.7',
    receta_domicilio_controlado: 'Av. Ignacio Pichardo Pagaza, Esq. Valle de los Toltecas No. 70',
    diagnostico_cie10: [{ codigo: 'R51', descripcion: 'Cefalea' }],
    medicamentos: [
      {
        nombre: 'Paracetamol',
        presentacion: 'Tableta 500 mg',
        dosis: '500 mg c/8h',
        via: 'Oral',
        duracion: '3 días',
        cantidad: '9'
      }
    ],
    doctor: {
      nombre: calibrationDoctor?.nombre || user?.nombre || '',
      rol: calibrationDoctor?.rol || user?.rol || 'doctor',
      cedula: calibrationDoctor?.cedula || '__________',
      universidad: calibrationDoctor?.universidad || '',
      prefijo_profesional: user?.prefijo_profesional || 'Dr.'
    }
  });

  if (!user) {
    if (currentView === 'login') {
      return (
        <Login
          onLoginSuccess={handleLoginSuccess}
          onBack={() => setCurrentView('landing')}
        />
      );
    }
    return <Landing onGoToLogin={() => setCurrentView('login')} />;
  }

  const userInitials = user.nombre
    ? user.nombre.trim().split(' ').filter(Boolean)
        .map((word) => word[0]).join('').slice(0, 2).toUpperCase()
    : 'DR';

  const consultationBackLabel = consultationReturnView === 'patients-history'
    ? 'Volver al Historial'
    : 'Volver al Dashboard';

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-brand">
            <div className="header-brand-icon">
              <img src="/assets/logos/logo-icon.jpeg" alt="CEG" className="header-logo-img" />
            </div>
            <div className="header-left">
              <h1>Clínica de Especialidades Guarneros</h1>
              <p className="header-subtitle">Sistema de Consulta Externa</p>
            </div>
          </div>
          <div className="header-right">
            {activeDrafts.length > 0 && (
              <div className="header-draft-badge">
                <span className="header-draft-badge-count">{activeDrafts.length}</span>
                <span className="header-draft-badge-text">borradores activos</span>
              </div>
            )}
            <div className="user-avatar" aria-hidden="true" title={user.nombre}>
              {userInitials}
            </div>
            <div className="user-info">
              <span className="user-name">{user.nombre}</span>
              <span className="user-role">{user.rol === 'admin' ? 'Administrador' : 'Doctor'}</span>
              <span className="user-cedula">@{user.username}</span>
            </div>
            <button onClick={handleLogout} className="btn btn-ghost-white">
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <main className="app-main">
        {currentView === 'dashboard' && (
          <Dashboard
            user={user}
            activeDrafts={activeDrafts}
            onResumeDraft={handleResumeDraft}
            onNewConsultation={handleNewConsultation}
            onViewPatients={handleViewPatients}
            onViewLaboratories={handleViewLaboratories}
            onViewAuditLog={canViewAuditLog ? handleViewAuditLog : null}
            onOpenCalibrationReceta={handleOpenCalibrationReceta}
          />
        )}
        {currentView === 'audit-log' && canViewAuditLog && (
          <AuditLogPanel onBack={handleBackToDashboard} />
        )}
        {currentView === 'new-consultation' && (
          <PatientForm
            key={`${editingConsultationId || 'new'}-${editingSessionKey || 'sessionless'}`}
            user={user}
            consultationId={editingConsultationId}
            draftSessionKey={editingSessionKey}
            onDraftsChanged={loadActiveDrafts}
            backLabel={consultationBackLabel}
            onBack={editingConsultationId ? handleExitConsultation : handleBackToDashboard}
            onSuccess={editingConsultationId ? handleExitConsultation : handleBackToDashboard}
          />
        )}
        {currentView === 'patients-history' && (
          <PatientsHistory
            onBack={handleBackToDashboard}
            onResumeConsultation={handleResumeConsultation}
            activeDrafts={activeDrafts}
          />
        )}
        {currentView === 'laboratories-dashboard' && (
          <LaboratoriesDashboard onBack={handleBackToDashboard} />
        )}
        {currentView === 'receta-calibration' && (
          <RecetaPreview consulta={buildCalibrationConsultation()} onClose={handleBackToDashboard} />
        )}
      </main>

      <footer className="app-footer">
        <p>&copy; {new Date().getFullYear()} Clínica de Especialidades Guarneros &mdash; Sistema de Consulta Externa v1.7</p>
        <div className="footer-badge">
          <span className="footer-dot" />
          Sistema en línea
        </div>
      </footer>
    </div>
  );
}

export default App;
